// src/utils/postLoginRedirect.js
//
// Shared post-login routing rule used by every login flow
// (LoginPage, DemoAccessPage, AcceptInvite).
//
// Decision tree (top to bottom — first match wins):
//   1.  no user/token                  → "/welcome"      (safety fallback)
//   2.  superadmin / is_superuser      → "/superadmin"
//   3.  is_first_login === true        → "/onboarding"   (never onboarded)
//   4.  otherwise                      → "/welcome"       (returning OR
//                                        triggered-but-abandoned onboarding)
//
// The first-vs-returning decision keys ONLY on `is_first_login`, which is
// SERVER TRUTH: the backend derives it as `!onboarding_started` (Session A's
// durable flag), flipped TRUE the moment onboarding is first SHOWN. It is NOT
// a timing heuristic and NOT a device-local flag — so a user who triggered
// but abandoned onboarding (onboarding_started TRUE, completed FALSE) is
// routed to the welcome page, never forced back into onboarding.
//
// Net flows:
//   first-ever login : login → /onboarding → (complete) → /welcome → dashboard
//   returning login  : login → /welcome → dashboard
// Both converge on welcome → dashboard.
//
// Token is taken as an EXPLICIT argument (not from localStorage) so the
// caller can avoid any "token-not-yet-persisted" race after login().

export async function decidePostLoginPath({ user, token, is_first_login } = {}) {
  // eslint-disable-next-line no-console
  console.log("[postLogin] user=", user?.id, "role=", user?.role, "is_first_login=", is_first_login);

  if (!user || !token) return "/welcome";

  // Superadmin bypass — always lands on the superadmin console.
  if (user.role === "superadmin" || user.is_superuser) {
    return "/superadmin";
  }

  // Durable first-vs-returning gate (Session A `onboarding_started`, surfaced
  // as is_first_login = !onboarding_started). Never onboarded → the intro;
  // everyone else → the daily KIRA welcome. No localStorage / timing gate.
  if (is_first_login === true) {
    return "/onboarding";
  }

  return "/welcome";
}

// Convenience — hard-navigates to the decided path.
export async function postLoginNavigate(args) {
  const path = await decidePostLoginPath(args);
  // eslint-disable-next-line no-console
  console.log("[postLogin] navigating to:", path);
  window.location.href = path;
}
